// The offline copy (docs/WEBMAIL.md, phase 3). The folders chosen in the settings (/api/settings) are kept on the
// device (store.ts) and read from there when the server cannot be reached:
//   sync      after every /api/folders answer, each offline folder whose change marker moved since it was last
//             listed (or that is not complete) is listed again (GET /api/offline/folder) and compared with the copy:
//             gone messages leave, flags are updated, missing messages come in batches newest first (POST
//             /api/offline/messages), then the attachments when the folder is kept with them. A message no offline
//             folder holds any more is deleted. One sync at a time across tabs (Web Locks).
//   reading   listings and messages come from the server while it answers; without an answer, from the copy. A
//             message in the copy is shown from it at once, then from the server. Attachments come from the copy
//             when they are in it.
// Storage: navigator.storage.persist() is asked once a sync stores something; when storage runs out the oldest
// attachments, then bodies, make room (store.withRoom), and mail as old as the newest one evicted (or as the batch
// that found nothing older to evict) is no longer fetched (kept_from) until the settings change.

import { useEffect, useState } from "preact/hooks";
import { type AppSettings, ApiError, type Fetching, type Folder, type FolderListing, type FoldersResponse, type Me, type Message, type MessagesResponse, type OfflineMessage, type SearchParams, api } from "./api";
import { API_BASE } from "./auth";
import { listingRow, matches, onlyInTrash, parseQuery, passesFilters, searchText, starredIn } from "./localsearch";
import { isNetworkError, isOnline } from "./net";
import { type CachedLocation, type CachedRow, type FolderState, StorageFull, copyGeneration, dateKey, db, deleteOfflineCopy, folderLocations, getMeta, heldMessages, setMeta, withRoom } from "./store";

/** Messages per POST /api/offline/messages, the most it takes (it stops an answer at about 8 MB of archived mail). */
const BATCH = 50;
/** Attachments larger than this are not kept (Yahoo's limit for a whole message is 25 MB). */
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const FILE_DOWNLOADS = 3;
/** A search in the copy is kept this long, so paging through it does not search again. */
const SEARCH_KEEP_MS = 60_000;

export interface OfflineStatus {
  /** The folder being brought up to date now, and how far it got. */
  syncing: { folder: string; what: "messages" | "attachments"; done: number; total: number } | null;
  /** Every offline folder's state on the device. */
  folders: FolderState[];
  /** Messages kept with their body. */
  kept: number;
  /** Why the last sync stopped (a lost connection is not reported: the next sync goes on). */
  error: string | null;
  /** Storage ran out: mail before this date is not kept (null: everything is). */
  keptFrom: string | null;
}

let status: OfflineStatus = { syncing: null, folders: [], kept: 0, error: null, keptFrom: null };
const watchers = new Set<(s: OfflineStatus) => void>();

function setStatus(patch: Partial<OfflineStatus>): void {
  status = { ...status, ...patch };
  watchers.forEach((fn) => fn(status));
}

export function useOfflineStatus(): OfflineStatus {
  const [s, set] = useState(status);
  useEffect(() => {
    watchers.add(set);
    set(status);
    return () => void watchers.delete(set);
  }, []);
  return s;
}

/** IndexedDB can be unavailable (private windows, storage turned off) or fail: the app then works online only. */
async function safe<T>(run: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await run();
  } catch (e) {
    console.warn("offline copy:", e);
    return fallback;
  }
}

/** Counts and folder states for the status, read from the copy. */
export async function refreshStatus(): Promise<void> {
  const d = db();
  const [folders, kept, keptFrom] = await safe(() => Promise.all([d.folders.toArray(), d.bodies.count(), getMeta<string>("kept_from")]), [[], 0, null] as const);
  setStatus({ folders: [...folders], kept, keptFrom });
}

// ---------------------------------------------------------------------------
// what the app keeps for starting without a connection
// ---------------------------------------------------------------------------

export const remembered = {
  folders: () => safe(() => getMeta<FoldersResponse>("folders"), null),
  me: () => safe(() => getMeta<Me>("me"), null),
  settings: () => safe(() => getMeta<AppSettings>("settings"), null),
  keepFolders: (res: FoldersResponse) => safe(() => setMeta("folders", res), undefined),
  keepMe: (me: Me) => safe(() => setMeta("me", me), undefined),
};

/** The settings from the server (kept on the device), or the ones kept when it cannot be reached. */
export async function loadSettings(): Promise<AppSettings | null> {
  try {
    const settings = await api.settings();
    await safe(() => setMeta("settings", settings), undefined);
    return settings;
  } catch (e) {
    if (isNetworkError(e)) return remembered.settings();
    throw e;
  }
}

/** New offline folders: saved on the server (only they: another device may have changed the other settings), then the
 * copy follows them (a storage limit reached before is tried again). */
export async function saveSettings(settings: AppSettings): Promise<AppSettings> {
  const saved = await api.saveSettings({ offline_folders: settings.offline_folders });
  await safe(async () => {
    await setMeta("settings", saved);
    await setMeta("kept_from", null);
    await db().folders.toCollection().modify({ complete: false }); // each folder looks again for what it left out
  }, undefined);
  setStatus({ keptFrom: null, error: null });
  return saved;
}

/** A change to settings the copy does not depend on (push_folders): saved on the server, which keeps the others as
 * they are, and kept on the device. */
export async function saveOtherSettings(change: Partial<AppSettings>): Promise<AppSettings> {
  const saved = await api.saveSettings(change);
  await safe(() => setMeta("settings", saved), undefined);
  return saved;
}

/** The settings with `folder` kept offline (with attachments, as a new folder starts) or not. */
export function keepFolder(settings: AppSettings, folder: string, keep: boolean): AppSettings {
  const others = settings.offline_folders.filter((f) => f.folder.toLowerCase() !== folder.toLowerCase());
  return { ...settings, offline_folders: keep ? [...others, { folder, attachments: true }] : others };
}

/** The settings with an offline folder kept with or without its attachments. */
export function keepAttachments(settings: AppSettings, folder: string, attachments: boolean): AppSettings {
  return { ...settings, offline_folders: settings.offline_folders.map((f) => (f.folder.toLowerCase() === folder.toLowerCase() ? { ...f, attachments } : f)) };
}

/** The offline folder setting of a folder, if it is kept. */
export function offlineSetting(settings: AppSettings | null, folder: string): AppSettings["offline_folders"][number] | null {
  return settings?.offline_folders.find((f) => f.folder.toLowerCase() === folder.toLowerCase()) ?? null;
}

/** Bumped when the copy is deleted: a sync still running then stores nothing more (it would make a new database). */
let epoch = 0;
let syncEpoch = 0;

class CopyDeleted extends Error {}

function stillWanted(): void {
  if (syncEpoch !== epoch) throw new CopyDeleted("The offline copy was deleted");
}

/** Sign-out: the copy leaves the device. */
export async function forgetOfflineCopy(): Promise<void> {
  epoch++;
  await safe(deleteOfflineCopy, undefined);
  lastSearch = null;
  unavailable.clear();
  setStatus({ syncing: null, folders: [], kept: 0, error: null, keptFrom: null });
}

// ---------------------------------------------------------------------------
// sync
// ---------------------------------------------------------------------------

let latest: Folder[] = [];
let running: Promise<void> | null = null;
let again = false;
/** Messages and attachments the server could not give this session: not asked for again until the next start. */
const unavailable = new Set<string>();
let askedToPersist = false;

/** Brings the copy up to date with these folders (from /api/folders). One sync at a time: a call during a sync makes
 * it run once more afterwards, with the newest folders. */
export function syncOffline(folders: Folder[]): Promise<void> {
  latest = folders;
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    try {
      do {
        again = false;
        await exclusive(() => runSync(latest));
      } while (again);
    } finally {
      running = null;
    }
  })();
  return running;
}

/** One sync per browser: another tab that is syncing is left to it. */
async function exclusive(run: () => Promise<void>): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.locks) return run();
  await navigator.locks.request(`ym-offline-sync:${API_BASE}`, { ifAvailable: true }, async (lock) => {
    if (lock) await run();
  });
}

interface Wanted {
  folder: Folder;
  attachments: boolean;
}

export async function runSync(folders: Folder[]): Promise<void> {
  if (!isOnline()) return;
  syncEpoch = epoch;
  const settings = await safe(() => getMeta<AppSettings>("settings"), null);
  if (!settings) return;
  const d = db();
  const byName = new Map(folders.map((f) => [f.name.toLowerCase(), f]));
  const wanted: Wanted[] = settings.offline_folders.flatMap((s) => {
    const folder = byName.get(s.folder.toLowerCase());
    return folder ? [{ folder, attachments: s.attachments }] : [];
  });
  let changed = false;
  try {
    const keep = new Set(wanted.map((w) => w.folder.name));
    for (const state of await d.folders.toArray()) {
      if (keep.has(state.name)) continue;
      await dropFolder(state.name);
      changed = true;
    }
    let error: string | null = null;
    for (const w of wanted) {
      const state = await d.folders.get(w.folder.name);
      if (state && state.complete && state.change === w.folder.change && state.attachments === w.attachments) continue;
      changed = true;
      await askToPersist();
      try {
        await syncFolder(w.folder.name, w.attachments);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) await dropFolder(w.folder.name); // gone from the archive
        else if (e instanceof ApiError) error = `${w.folder.name}: ${e.message}`;
        else throw e;
      }
    }
    if (changed) await collectGarbage(wanted);
    setStatus({ error });
  } catch (e) {
    if (e instanceof CopyDeleted) return;
    if (e instanceof StorageFull) setStatus({ error: "The device's storage for the app is full: the oldest mail is not kept." });
    else if (!isNetworkError(e)) {
      console.error("offline copy:", e);
      setStatus({ error: e instanceof Error ? e.message : String(e) });
    }
  } finally {
    setStatus({ syncing: null });
    if (changed && syncEpoch === epoch) {
      lastSearch = null;
      await refreshStatus();
    }
  }
}

async function dropFolder(name: string): Promise<void> {
  const d = db();
  await d.transaction("rw", d.locations, d.folders, async () => {
    await folderLocations(name).delete();
    await d.folders.delete(name);
  });
}

function location(folder: string, m: FolderListing["messages"][number]): CachedLocation {
  return { folder, id: m.id, date_key: dateKey(m.date), flags: m.flags };
}

async function syncFolder(name: string, attachments: boolean): Promise<void> {
  const d = db();
  const listing = await api.offlineFolder(name);
  // a message an action in the outbox waits for stays as the action left it (the server has not had the action yet)
  const held = await heldMessages();
  listing.messages = listing.messages.filter((m) => !held.has(m.id));
  const listed = new Map(listing.messages.map((m) => [m.id, m]));
  const total = listing.messages.length;
  setStatus({ syncing: { folder: name, what: "messages", done: 0, total } });

  // the folder's places and flags as listed; a message the copy holds already (from another folder) joins at once
  const known = (await folderLocations(name).toArray()).filter((l) => !held.has(l.id));
  const knownIds = new Set(known.map((l) => l.id));
  const rowIds = new Set(await d.rows.toCollection().primaryKeys());
  const previous = await d.folders.get(name);
  await d.transaction("rw", d.locations, d.folders, async () => {
    await d.locations.bulkDelete(known.filter((l) => !listed.has(l.id)).map((l) => [name, l.id] as [string, string]));
    const updated = known.flatMap((l) => {
      const m = listed.get(l.id);
      return m && (dateKey(m.date) !== l.date_key || m.flags.join(" ") !== l.flags.join(" ")) ? [location(name, m)] : [];
    });
    const joined = listing.messages.filter((m) => !knownIds.has(m.id) && rowIds.has(m.id)).map((m) => location(name, m));
    await d.locations.bulkPut([...updated, ...joined]);
    await d.folders.put({ name, change: listing.change, complete: false, attachments, messages: total, synced_at: previous?.synced_at ?? null });
  });

  // the messages without a body, newest first, in batches (storage ran out at kept_from: not what is older)
  const keptFrom = await getMeta<string>("kept_from");
  const kept = (m: FolderListing["messages"][number]) => keptFrom === null || dateKey(m.date) > keptFrom;
  const bodyIds = new Set(await d.bodies.toCollection().primaryKeys());
  const queue = listing.messages.filter((m) => kept(m) && !bodyIds.has(m.id) && !unavailable.has(m.id)).map((m) => m.id);
  let done = total - queue.length;
  setStatus({ syncing: { folder: name, what: "messages", done, total } });
  while (queue.length > 0) {
    const batch = await api.offlineMessages(queue.splice(0, BATCH));
    batch.missing.forEach((id) => unavailable.add(id));
    queue.unshift(...batch.more);
    if (batch.messages.length > 0) await storeMessages(name, batch.messages, listed);
    else if (batch.missing.length === 0) break; // nothing came: never ask for the same ones forever
    done += batch.messages.length + batch.missing.length;
    setStatus({ syncing: { folder: name, what: "messages", done, total } });
  }

  if (attachments) await syncAttachments(name, listing.messages.filter(kept).map((m) => m.id));
  await d.folders.put({ name, change: listing.change, complete: true, attachments, messages: total, synced_at: new Date().toISOString() });
}

function rowOf(m: OfflineMessage): CachedRow {
  const { html: _html, body_text: _text, ...row } = m;
  return row;
}

async function storeMessages(folder: string, messages: OfflineMessage[], listed: Map<string, FolderListing["messages"][number]>): Promise<void> {
  stillWanted();
  const d = db();
  const dates = messages.map((m) => dateKey(m.date)).sort();
  try {
    await withRoom(
      dates[0],
      () =>
        d.transaction("rw", [d.rows, d.bodies, d.texts, d.locations], async () => {
          await d.rows.bulkPut(messages.map(rowOf));
          await d.bodies.bulkPut(messages.map((m) => ({ id: m.id, date_key: dateKey(m.date), html: m.html, body_text: m.body_text })));
          await d.texts.bulkPut(messages.map((m) => ({ id: m.id, date_key: dateKey(m.date), text: searchText(m, m.body_text) })));
          await d.locations.bulkPut(messages.flatMap((m) => (listed.has(m.id) ? [location(folder, listed.get(m.id)!)] : [])));
        }),
      noteEvicted,
    );
  } catch (e) {
    // nothing older left to make room with: this batch and older mail are not kept (not fetched again and again)
    if (e instanceof StorageFull) noteEvicted(dates[dates.length - 1]);
    throw e;
  } finally {
    await settleEvictions();
  }
}

/** The newest message evicted while making room in this sync: mail older than it is not fetched again. */
let evictedUpTo: string | null = null;

function noteEvicted(key: string): void {
  if (evictedUpTo === null || key > evictedUpTo) evictedUpTo = key;
}

async function settleEvictions(): Promise<void> {
  if (evictedUpTo === null) return;
  const before = await getMeta<string>("kept_from");
  if (before === null || evictedUpTo > before) {
    await setMeta("kept_from", evictedUpTo);
    setStatus({ keptFrom: evictedUpTo });
  }
  evictedUpTo = null;
}

async function syncAttachments(folder: string, ids: string[]): Promise<void> {
  const d = db();
  const have = new Set((await d.files.toCollection().primaryKeys()).map(([id, part]) => `${id}\n${part}`));
  const wanted: { id: string; part: number; date_key: string; mime: string }[] = [];
  for (let i = 0; i < ids.length; i += 500) {
    for (const row of await d.rows.bulkGet(ids.slice(i, i + 500))) {
      if (!row) continue;
      for (const a of row.attachments) {
        const key = `${row.id}\n${a.part_index}`;
        if (a.inline || (a.size ?? 0) > MAX_FILE_BYTES || have.has(key) || unavailable.has(key)) continue;
        wanted.push({ id: row.id, part: a.part_index, date_key: dateKey(row.date), mime: a.mime || "application/octet-stream" });
      }
    }
  }
  let done = 0;
  const total = wanted.length;
  setStatus({ syncing: { folder, what: "attachments", done, total } });
  // a few downloads at a time; the first failure (no connection, storage full) stops them all
  let failure: { error: unknown } | null = null;
  const next = async (): Promise<void> => {
    for (let f = wanted.shift(); f && !failure; f = wanted.shift()) {
      try {
        const data = await (await api.attachment(f.id, f.part)).arrayBuffer();
        const file = { ...f, data };
        stillWanted();
        await withRoom(f.date_key, () => d.files.put(file), noteEvicted);
      } catch (e) {
        if (e instanceof StorageFull) noteEvicted(f.date_key);
        if (!(e instanceof ApiError)) {
          failure ??= { error: e };
          return;
        }
        unavailable.add(`${f.id}\n${f.part}`);
      }
      done++;
      setStatus({ syncing: { folder, what: "attachments", done, total } });
    }
  };
  await Promise.all(Array.from({ length: FILE_DOWNLOADS }, next));
  await settleEvictions();
  if (failure) throw (failure as { error: unknown }).error;
}

/** Deletes what no offline folder holds any more, and attachments of mail kept only in folders kept without them. */
async function collectGarbage(wanted: Wanted[]): Promise<void> {
  const d = db();
  const folders = new Map<string, string[]>();
  for (const l of await d.locations.toArray()) folders.set(l.id, [...(folders.get(l.id) ?? []), l.folder]);
  const held = await heldMessages(); // trashed in the outbox: kept until the trash is sent (or cancelled)
  const orphans = (await d.rows.toCollection().primaryKeys()).filter((id) => !folders.has(id) && !held.has(id));
  if (orphans.length > 0) {
    await d.transaction("rw", [d.rows, d.bodies, d.texts, d.files], async () => {
      await d.rows.bulkDelete(orphans);
      await d.bodies.bulkDelete(orphans);
      await d.texts.bulkDelete(orphans);
      await d.files.where("id").anyOf(orphans).delete();
    });
  }
  const withFiles = new Set(wanted.filter((w) => w.attachments).map((w) => w.folder.name));
  const fileIds = (await d.files.orderBy("id").uniqueKeys()) as string[];
  const strip = fileIds.filter((id) => !(folders.get(id) ?? []).some((f) => withFiles.has(f)));
  if (strip.length > 0) await d.files.where("id").anyOf(strip).delete();
}

/** Asked once a sync is about to store mail: storage the browser may not clear when space runs low. Browsers decide
 * by themselves (Safari, Chrome, Edge, Brave ask no question); the settings screen shows the answer. */
async function askToPersist(): Promise<void> {
  if (askedToPersist || typeof navigator === "undefined" || !navigator.storage?.persist) return;
  askedToPersist = true;
  try {
    if (!(await navigator.storage.persisted())) await navigator.storage.persist();
  } catch {
    // not supported: the copy is best effort
  }
}

// ---------------------------------------------------------------------------
// reading
// ---------------------------------------------------------------------------

/** Neither the server nor the copy has it. */
export class NotKept extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NotKept";
  }
}

export type Page = MessagesResponse & { offline?: boolean };

const SEARCH_FIELDS: (keyof SearchParams)[] = ["q", "from", "to", "sent_to", "after", "before", "has_attachment", "include_deleted", "exclude_trash", "flagged"];

/** A folder listing (the app's folder view) rather than a search. */
export function isFolderListing(p: SearchParams): p is SearchParams & { folder: string } {
  return Boolean(p.folder) && SEARCH_FIELDS.every((k) => !p[k]);
}

/** A page of a listing or search: from the server, or from the copy when the server cannot be reached. */
export async function listMessages(params: SearchParams, limit: number, offset: number): Promise<Page> {
  if (isOnline()) {
    try {
      return await api.messages({ ...params, limit, offset });
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  if (isFolderListing(params)) {
    const page = await safe(() => folderPage(params.folder, limit, offset), null);
    if (!page) throw new NotKept(`No connection to the server, and ${params.folder} is not kept on this device.`);
    return { ...page, offline: true };
  }
  const page = await safe(() => searchCopy(params, limit, offset), null);
  if (!page) throw new NotKept(params.folder ? `No connection to the server, and ${params.folder} is not kept on this device.` : "No connection to the server.");
  return { ...page, offline: true };
}

async function folderState(name: string): Promise<FolderState | null> {
  const d = db();
  return (await d.folders.get(name)) ?? (await d.folders.filter((f) => f.name.toLowerCase() === name.toLowerCase()).first()) ?? null;
}

function lastSync(state: FolderState | null): MessagesResponse["last_sync"] {
  return { synced_at: state?.synced_at ?? null, run_ok: null, run_error: null };
}

export async function folderPage(name: string, limit: number, offset: number): Promise<MessagesResponse | null> {
  const state = await folderState(name);
  if (!state) return null;
  const d = db();
  const total = await folderLocations(state.name).count();
  const locs = await folderLocations(state.name).reverse().offset(offset).limit(limit).toArray();
  const rows = await d.rows.bulkGet(locs.map((l) => l.id));
  const messages = locs.flatMap((l, i) => (rows[i] ? [listingRow(rows[i], l.flags)] : []));
  return { total, capped: false, offset, limit, messages, last_sync: lastSync(state) };
}

let lastSearch: { key: string; at: number; generation: number; hits: CachedRow[]; flags: Map<string, string[]> } | null = null;

/** Search the copy: the words in the texts, the filters on the rows and the star on the flags, newest first; null when
 * the search is in a folder that is not kept. */
export async function searchCopy(params: SearchParams, limit: number, offset: number): Promise<MessagesResponse | null> {
  const key = JSON.stringify(params);
  if (!lastSearch || lastSearch.key !== key || lastSearch.generation !== copyGeneration() || Date.now() - lastSearch.at > SEARCH_KEEP_MS) {
    const d = db();
    const state = params.folder ? await folderState(params.folder) : null;
    if (params.folder && !state) return null;
    const flags = new Map<string, string[]>();
    const places = new Map<string, CachedLocation[]>();
    const inFolder = new Set<string>();
    for (const l of await d.locations.toArray()) {
      flags.set(l.id, Array.from(new Set([...(flags.get(l.id) ?? []), ...l.flags])).sort());
      places.set(l.id, [...(places.get(l.id) ?? []), l]);
      if (state && l.folder === state.name) inFolder.add(l.id);
    }
    const offline = new Set((await d.folders.toArray()).map((s) => s.name));
    // the star in the folder searched, else in any but Trash and spam, as the server counts it; Trash as it does too
    const byFolders = (row: CachedRow) => {
      if (params.flagged === undefined && !params.exclude_trash) return true;
      const folders = mergeFolders(row.folders, places.get(row.id) ?? [], offline);
      return (params.flagged === undefined || starredIn(folders, state?.name ?? null) === params.flagged) && !(params.exclude_trash && onlyInTrash(folders));
    };
    const candidates: CachedRow[] = [];
    await d.rows.each((row) => {
      if ((!state || inFolder.has(row.id)) && passesFilters(row, params) && byFolders(row)) candidates.push(row);
    });
    const query = parseQuery(params.q ?? "");
    let hits = candidates;
    if (query.length > 0) {
      hits = [];
      for (let i = 0; i < candidates.length; i += 500) {
        const chunk = candidates.slice(i, i + 500);
        const texts = await d.texts.bulkGet(chunk.map((r) => r.id));
        chunk.forEach((r, n) => matches(query, texts[n]?.text ?? searchText(r, null), !!params.exact) && hits.push(r));
      }
    }
    hits.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
    lastSearch = { key, at: Date.now(), generation: copyGeneration(), hits, flags };
  }
  const { hits, flags } = lastSearch;
  const messages = hits.slice(offset, offset + limit).map((r) => listingRow(r, flags.get(r.id) ?? (r.folders.length > 0 ? r.flags : null)));
  return { total: hits.length, capped: false, offset, limit, messages, last_sync: lastSync(null) };
}

/** A message from the copy, its folders and flags as the copy knows them; null when its body is not kept. */
export async function cachedMessage(id: string): Promise<Message | null> {
  return safe(async () => {
    const d = db();
    const [row, body, locs, states] = await Promise.all([d.rows.get(id), d.bodies.get(id), d.locations.where("id").equals(id).toArray(), d.folders.toArray()]);
    if (!row || !body) return null;
    const folders = mergeFolders(row.folders, locs, new Set(states.map((s) => s.name)));
    const flags = folders.length > 0 ? Array.from(new Set(folders.flatMap((f) => f.flags))).sort() : null;
    return {
      ...row,
      folders,
      flags: flags ?? [],
      unread: flags !== null && !flags.includes("\\Seen"),
      flagged: flags?.includes("\\Flagged") ?? false,
      html: body.html,
      body_text: body.body_text,
    };
  }, null);
}

/** The message's folders when it was stored, brought up to date for the offline folders: their flags as last listed,
 * and an offline folder the message has left dropped. */
export function mergeFolders(stored: Message["folders"], locs: CachedLocation[], offline: Set<string>): Message["folders"] {
  const here = new Map(locs.map((l) => [l.folder, l.flags]));
  const out = stored.flatMap((f) => (offline.has(f.folder) ? (here.has(f.folder) ? [{ folder: f.folder, flags: here.get(f.folder)! }] : []) : [f]));
  for (const l of locs) if (!out.some((f) => f.folder === l.folder)) out.push({ folder: l.folder, flags: l.flags });
  return out;
}

/** A message from the server, or from the copy when the server cannot be reached (writing a reply offline). */
export async function loadMessage(id: string): Promise<Message> {
  if (isOnline()) {
    try {
      return await api.message(id);
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  const kept = await cachedMessage(id);
  if (kept) return kept;
  throw new NotKept("No connection to the server, and this message is not kept on this device.");
}

/** An attachment from the copy, else from the server (followed and stopped with `fetching`). */
export async function loadAttachment(id: string, part: number, fetching: Fetching = {}): Promise<Blob> {
  const file = await safe(() => db().files.get([id, part]), undefined);
  if (file) return new Blob([file.data], { type: file.mime });
  try {
    return await api.attachment(id, part, fetching);
  } catch (e) {
    if (isNetworkError(e)) throw new NotKept("No connection to the server, and this attachment is not kept on this device.");
    throw e;
  }
}
